import React from 'react';
import { Check } from 'lucide-react';

/**
 * Canonical labels for the 4-step study wizard, shared across the free and
 * paid flows (UseCasePicker, BuildAudienceScreen, FillStudyScreen,
 * ReviewScreen) so the same step is never named three different ways.
 */
export const WIZARD_STEP_LABELS: string[] = ['Use case', 'Audience', 'Set up', 'Review'];

const CIRCLE_BASE =
  'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-medium transition-colors';
const CIRCLE_DONE = 'bg-emerald-500 text-white';
const CIRCLE_ACTIVE = 'bg-[#111] text-white';
// A not-yet-reached step is an empty outline on the page canvas, not a filled grey chip —
// the fill is what makes the current step (solid black) and the done steps (solid green)
// read as the only steps that "exist" yet.
const CIRCLE_PENDING = 'border border-black/[0.08] bg-white text-[#b0b0b0]';

const LABEL_BASE = 'text-[13px] font-medium transition-colors';
const LABEL_REACHED = 'text-[#111]';
const LABEL_PENDING = 'text-[#b0b0b0]';

const CONNECTOR_BASE = 'mx-3 h-[1.5px] w-10 rounded-full transition-colors';
const CONNECTOR_DONE = 'bg-[#111]';
const CONNECTOR_PENDING = 'bg-[#ececec]';

// Left-aligned at every width: the caption reads as the page's eyebrow, sitting with the
// heading below it, while the step circles stay centred above. Centring it to match the
// circles detaches it from the copy it introduces.
const CAPTION = 'mt-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8a8a8a]';

// Below md only the current step is rendered — see the note on the <ol>.
const STEP_ITEM = 'items-center md:flex';
const STEP_ITEM_CURRENT = 'flex';
const STEP_ITEM_OTHER = 'hidden';

/**
 * Step indicator for the study wizard: completed steps collapse to a green
 * check, the current step is a solid black numbered circle, and later steps
 * stay muted grey. A "STEP x OF y" caption sits underneath so the position is
 * legible without decoding the circles.
 *
 * This is a PURE progress indicator — it is intentionally NOT clickable. Back
 * navigation is owned by the WizardNav footer bar (one Back per screen), so there is
 * exactly one way to go back and no competing affordances.
 */
const StepIndicator: React.FC<{
  step: number;
  labels: string[];
  skippedSteps?: number[];
}> = ({ step, labels, skippedSteps = [] }) => (
  <div className="mb-6 md:mb-8">
    {/* ONE list, two layouts. From md up: the full horizontal track, every step labelled.
        Below md a phone gets a single line — only the CURRENT step's circle and label —
        because four labelled rows plus the caption ate ~21% of the viewport above the
        fold to say what "2 · Audience · STEP 2 OF 4" already says. The other steps are
        display:none, so they are out of the a11y tree too rather than read out twice.
        Rendering separate mobile/desktop markup would instead put every label in the DOM
        twice — bad for screen readers, and it makes getByText ambiguous. */}
    <ol className="flex items-center md:justify-center">
      {labels.map((text, i) => {
        const n = i + 1;
        // A skipped step (e.g. "Set up" for chat, which has no form) is behind the
        // current one but was never completed — it must NOT claim a green check. It
        // stays muted, like a step not yet reached.
        const skipped = skippedSteps.includes(n);
        const done = n < step && !skipped;
        const active = n === step;
        const last = i === labels.length - 1;
        return (
          <li key={text} className={`${STEP_ITEM} ${active ? STEP_ITEM_CURRENT : STEP_ITEM_OTHER}`}>
            <span
              className={[
                CIRCLE_BASE,
                done ? CIRCLE_DONE : active ? CIRCLE_ACTIVE : CIRCLE_PENDING,
              ].join(' ')}
            >
              {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : n}
            </span>

            {/* The label needs air from its circle at BOTH widths — md:ml-0 ran them
                together ("2Audience"). */}
            <div className="ml-3 md:ml-2.5">
              <span className={`${LABEL_BASE} ${done || active ? LABEL_REACHED : LABEL_PENDING}`}>
                {text}
              </span>
            </div>

            {!last && (
              <span
                className={`hidden md:inline-block ${CONNECTOR_BASE} ${done ? CONNECTOR_DONE : CONNECTOR_PENDING}`}
              />
            )}
          </li>
        );
      })}
    </ol>

    <div className={CAPTION}>
      Step {step} of {labels.length}
    </div>
  </div>
);

export default StepIndicator;
